import React from 'react';
import { JsonViewer, defineDataType } from '@textea/json-viewer';
import { getDocumentType, getDocumentConfig } from './documentConfig';
import { openDocumentInNewTab } from './documentUtils';
import TiffPreview from './TiffPreview';

/**
 * TypingResponse Component
 *
 * Displays JSON data in an interactive tree view with expand/collapse functionality
 * Uses @textea/json-viewer for better user experience with document rendering support
 * Renders different document types (images, PDFs, TIFF, etc.) with appropriate icons
 *
 * @param {Object} data - The JSON data to display
 */
const TypingResponse = ({ data }) => {
  // Define custom data type for document values (images, PDFs, etc.)
  const documentDataType = defineDataType({
    is: (value, path) => {
      // Check if this is a string value in a document_value or documentValue field
      const lastKey = path && path.length > 0 ? path[path.length - 1] : null;
      const isDocumentValueField =
        lastKey === 'document' || lastKey === 'documentValue';
      const hasDataUri = typeof value === 'string' && value.startsWith('data:');

      return isDocumentValueField && hasDataUri;
    },
    Component: (props) => {
      const docType = getDocumentType(props.value);
      const config = getDocumentConfig(docType);

      // Render TIFF preview using canvas
      if (docType === 'tiff') {
        return <TiffPreview value={props.value} docType={docType} />;
      }

      // Render image preview for images only
      if (config.renderPreview) {
        return (
          <img
            src={props.value}
            alt="Document"
            className="cursor-pointer mt-2"
            style={{
              maxWidth: '90px',
              maxHeight: '90px',
            }}
            onError={(e) => {
              console.error('❌ Image load failed');
              e.target.style.display = 'none';
            }}
            onClick={() => openDocumentInNewTab(props.value, docType)}
            title="Click to open in new tab"
          />
        );
      }

      // Render icon for PDFs and other documents
      return (
        <div
          className="d-inline-flex gap-2 px-2 py-2 border cursor-pointer mt-2 br_7 align-items-center"
          style={{
            backgroundColor: config.bgColor,
            transition: 'all 0.2s ease',
            maxWidth: '200px',
          }}
          onClick={() => openDocumentInNewTab(props.value, docType)}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.02)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = 'none';
          }}
          title="Click to open in new tab"
        >
          <span className="font_24">{config.icon}</span>
          <div className="d-flex flex-column overflow-hidden">
            <span
              className="font_11 font_500"
              style={{
                color: '#666',
              }}
            >
              {config.label}
            </span>
            <span
              className="font_10"
              style={{
                color: '#999',
              }}
            >
              Click to preview
            </span>
          </div>
        </div>
      );
    },
  });

  return (
    <div className="bg-white p-3">
      <JsonViewer
        value={data || {}}
        theme="light"
        defaultInspectDepth={10}
        enableClipboard={true}
        displayDataTypes={false}
        displaySize={false}
        quotesOnKeys={false}
        valueTypes={[documentDataType]}
        style={{
          fontSize: '14px',
          fontFamily: 'Consolas, Monaco, "Courier New", monospace',
        }}
      />
    </div>
  );
};

export default TypingResponse;
